import AutoAwesomeIcon from '@material-design-icons/svg/outlined/auto_awesome.svg?react';
import CalendarTodayIcon from '@material-design-icons/svg/outlined/calendar_today.svg?react';
import CloseIcon from '@material-design-icons/svg/outlined/close.svg?react';
import DeleteIcon from '@material-design-icons/svg/outlined/delete.svg?react';
import EditIcon from '@material-design-icons/svg/outlined/edit.svg?react';
import KeyboardArrowDownIcon from '@material-design-icons/svg/outlined/keyboard_arrow_down.svg?react';
import ReplyIcon from '@material-design-icons/svg/outlined/reply.svg?react';
import SendIcon from '@material-design-icons/svg/outlined/send.svg?react';

import ThumbUpIcon from '@material-design-icons/svg/outlined/thumb_up.svg?react';
import TuneIcon from '@material-design-icons/svg/outlined/tune.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import StarIcon from '@material-design-icons/svg/filled/star.svg?react';
import StarBorderIcon from '@material-design-icons/svg/outlined/star.svg?react';
import * as Popover from '@radix-ui/react-popover';
import { clamp, range } from 'es-toolkit/math';
import { type ReactNode, useEffect, useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { cn } from '../../lib/utils';
import { useOptionalDappContext } from '../DappLayout';
import { Button } from '../ui/button';
import { DashboardHeader } from './DashboardHeader';
import { formatDigestWindow } from './dashboardReviewFormatting';
import {
  type RatingFilter,
  type ReviewFilters,
  type ReviewRow,
  type ReviewsPageData,
  type SortOption,
  type TimeFilter,
  type WeeklyDigest,
  useDashboardReviewsData,
} from './useDashboardReviewsData';

const TIME_FILTER_LABEL: Record<TimeFilter, string> = {
  day: '1 day',
  week: '7 days',
  month: '30 days',
};

const SORT_LABEL: Record<SortOption, string> = {
  newest: 'Newest',
  oldest: 'Oldest',
  highest: 'Highest rated',
  lowest: 'Lowest rated',
};

export const DashboardReviews = () => {
  const dappId = useOptionalDappContext()?.dappId;
  const {
    canRender,
    isChecking,
    dappName,
    data,
    displayData,
    error,
    isLoading,
    digest,
    isDigestLoading,
    reviewsByRating,
    totalRatings,
    totalPages,
    currentPage,
    isInitialLoad,
    hasError,
    showDigestPanel,
    storeListingMissing,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    activeFilterCount,
    mutatingReviewId,
    handleNextPage,
    handlePreviousPage,
    handleRefresh,
    handleSaveReply,
    handleDeleteReply,
  } = useDashboardReviewsData({ dappId });

  usePageTitle(
    dappId && dappName
      ? `${dappName} - Ratings & Reviews | Publishing Portal`
      : 'Ratings & Reviews | Publishing Portal',
  );

  if (isChecking || !canRender) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <DashboardHeader
        icon={<StarBorderIcon className="size-4 fill-white" />}
        title="Ratings & Reviews"
      />

      <div className="flex-1 overflow-auto rounded-b-xl bg-[#161717]">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-5 py-8 lg:px-8">
          {!dappId ? (
            <EmptyState title="No dApp selected" />
          ) : (
            <>
              <RatingAnalyticsPanel
                data={data}
                isInitialLoad={isInitialLoad}
                hasError={hasError}
                storeListingMissing={storeListingMissing}
                totalRatings={totalRatings}
                reviewsByRating={reviewsByRating}
                onRefresh={handleRefresh}
                isRefreshing={isLoading}
              />

              <div
                className={cn(
                  'grid items-start gap-6',
                  showDigestPanel
                    ? 'lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]'
                    : 'lg:grid-cols-1',
                )}
              >
                {showDigestPanel && (
                  <WeeklyDigestPanel
                    digest={digest}
                    isLoading={isDigestLoading}
                  />
                )}

                <UserReviewsPanel
                  data={data}
                  displayData={displayData}
                  error={error}
                  isLoading={isLoading}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onNext={handleNextPage}
                  onPrevious={handlePreviousPage}
                  onRetry={handleRefresh}
                  isInitialLoad={isInitialLoad}
                  hasError={hasError}
                  filters={filters}
                  onFiltersChange={setFilters}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  activeFilterCount={activeFilterCount}
                  mutatingReviewId={mutatingReviewId}
                  onSaveReply={handleSaveReply}
                  onDeleteReply={handleDeleteReply}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CARD_CLASS =
  'rounded-2xl border border-white/[0.06] bg-black/35 p-5 shadow-[0_0.375px_0.375px_0_inset_rgba(255,255,255,0.17)]';

function RatingAnalyticsPanel({
  data,
  isInitialLoad,
  hasError,
  storeListingMissing,
  totalRatings,
  reviewsByRating,
  onRefresh,
  isRefreshing,
}: {
  data: ReviewsPageData | null;
  isInitialLoad: boolean;
  hasError: boolean;
  storeListingMissing: boolean;
  totalRatings: number;
  reviewsByRating: number[];
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  const firstReleaseLabel = data?.firstReleaseDate
    ? formatFullDate(data.firstReleaseDate)
    : null;
  const metricsUnavailable = hasError || storeListingMissing;
  const replyCount = data?.summary.replyCount ?? null;

  return (
    <section className={CARD_CLASS}>
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-white">
            <StarBorderIcon className="size-4 fill-current" />
            Rating Analytics
          </div>
          {isInitialLoad ? (
            <div
              className="mt-1.5 h-3 w-44 animate-pulse rounded bg-neutral-800"
              role="status"
              aria-label="Loading release date"
            />
          ) : firstReleaseLabel ? (
            <div className="mt-1 text-xs text-neutral-500">
              Since First Release ({firstReleaseLabel})
            </div>
          ) : null}
        </div>
        <Button
          variant="outline"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="h-8 gap-2 border-neutral-700 bg-neutral-950 px-3 text-xs text-neutral-200 hover:bg-neutral-900"
        >
          <RefreshIcon className="size-4 fill-current" />
          Refresh
        </Button>
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="rounded-xl bg-neutral-950/70 p-5">
          <div className="grid items-center gap-5 sm:grid-cols-[auto_minmax(0,1fr)]">
            {isInitialLoad ? (
              <RatingScoreSkeleton />
            ) : (
              <RatingScore
                rating={data?.summary.rating ?? null}
                isUnavailable={metricsUnavailable}
              />
            )}
            {isInitialLoad ? (
              <RatingDistributionSkeleton />
            ) : (
              <RatingDistribution
                buckets={reviewsByRating}
                total={totalRatings}
                isUnavailable={metricsUnavailable}
              />
            )}
          </div>
        </div>

        <AnalyticsCard
          icon={<StarBorderIcon className="size-6 fill-current" />}
          label={totalRatings === 1 ? 'Total Review' : 'Total Reviews'}
          value={metricsUnavailable ? '—' : totalRatings.toLocaleString()}
          isLoading={isInitialLoad}
        />
        <AnalyticsCard
          icon={<ReplyIcon className="size-6 fill-current" />}
          label={replyCount === 1 ? 'Reply' : 'Replies'}
          value={
            metricsUnavailable || replyCount === null
              ? '—'
              : replyCount.toLocaleString()
          }
          isLoading={isInitialLoad}
        />
      </div>
    </section>
  );
}

function WeeklyDigestPanel({
  digest,
  isLoading,
}: {
  digest: WeeklyDigest | null;
  isLoading: boolean;
}) {
  const isSkeleton = isLoading && !digest;

  return (
    <section className={CARD_CLASS}>
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-white">
            <CalendarTodayIcon className="size-4 fill-current" />
            Weekly Digest
          </div>
          {isSkeleton ? (
            <div
              className="mt-1.5 h-3 w-44 animate-pulse rounded-full bg-neutral-800"
              role="status"
              aria-label="Loading digest window"
            />
          ) : digest ? (
            <div className="mt-1 text-xs text-neutral-500">
              {formatDigestWindow(digest.windowStart, digest.windowEnd)}
            </div>
          ) : null}
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-neutral-500">
          <AutoAwesomeIcon className="size-3.5 fill-current" />
          AI summary
        </span>
      </header>

      <div className="mt-5">
        {isSkeleton ? (
          <div
            role="status"
            aria-label="Loading weekly digest"
            className="space-y-2.5"
          >
            <div className="h-3.5 w-full animate-pulse rounded-full bg-neutral-800" />
            <div className="h-3.5 w-[94%] animate-pulse rounded-full bg-neutral-800" />
            <div className="h-3.5 w-[88%] animate-pulse rounded-full bg-neutral-800" />
            <div className="h-3.5 w-[72%] animate-pulse rounded-full bg-neutral-800" />
          </div>
        ) : digest?.summary ? (
          <p className="whitespace-pre-line text-sm leading-relaxed text-neutral-200">
            {digest.summary}
          </p>
        ) : null}
      </div>

      <div className="mt-5 border-t border-white/[0.05] pt-4">
        {isSkeleton ? (
          <div
            className="h-3 w-28 animate-pulse rounded-full bg-neutral-800"
            role="status"
            aria-label="Loading reviews label"
          />
        ) : (
          <div className="text-xs text-neutral-500">Reviews This Week</div>
        )}
        <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-white/[0.06] bg-neutral-950/70 px-3 py-2 text-xs text-neutral-200">
          <ThumbUpIcon className="size-3.5 fill-current text-neutral-400" />
          {isSkeleton ? (
            <span className="inline-block h-3 w-20 animate-pulse rounded-full bg-neutral-800" />
          ) : (
            <span className="tabular-nums">
              {(digest?.inputReviewCount ?? 0).toLocaleString()}{' '}
              {digest?.inputReviewCount === 1 ? 'review' : 'reviews'}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

function UserReviewsPanel({
  data,
  displayData,
  error,
  isLoading,
  currentPage,
  totalPages,
  onNext,
  onPrevious,
  onRetry,
  isInitialLoad,
  hasError,
  filters,
  onFiltersChange,
  sortBy,
  onSortChange,
  activeFilterCount,
  mutatingReviewId,
  onSaveReply,
  onDeleteReply,
}: {
  data: ReviewsPageData | null;
  displayData: ReviewsPageData | null;
  error: string | null;
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  onNext: () => void;
  onPrevious: () => void;
  onRetry: () => void;
  isInitialLoad: boolean;
  hasError: boolean;
  filters: ReviewFilters;
  onFiltersChange: (next: ReviewFilters) => void;
  sortBy: SortOption;
  onSortChange: (next: SortOption) => void;
  activeFilterCount: number;
  mutatingReviewId: string | null;
  onSaveReply: (review: ReviewRow, reply: string) => Promise<boolean>;
  onDeleteReply: (review: ReviewRow) => Promise<void>;
}) {
  const storeListingMissing = data !== null && !data.storeListingFound;

  const clearRating = () => onFiltersChange({ ...filters, minRating: null });
  const clearTime = () => onFiltersChange({ ...filters, timeWindow: null });

  return (
    <section className={cn(CARD_CLASS, 'flex flex-col')}>
      <header className="flex items-center gap-2 text-sm font-medium text-white">
        <StarBorderIcon className="size-4 fill-current" />
        User Reviews
      </header>

      {!storeListingMissing && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-b border-white/[0.05] pb-4 text-xs">
          <FilterPopover
            filters={filters}
            onChange={onFiltersChange}
            activeCount={activeFilterCount}
          />

          {filters.minRating !== null && (
            <FilterChip
              ariaLabel={`Remove rating filter ${filters.minRating} and up`}
              onRemove={clearRating}
            >
              <ChipStars rating={filters.minRating} />
              <span>&amp; Up</span>
            </FilterChip>
          )}
          {filters.timeWindow !== null && (
            <FilterChip
              ariaLabel={`Remove time filter ${TIME_FILTER_LABEL[filters.timeWindow]}`}
              onRemove={clearTime}
            >
              <span>{TIME_FILTER_LABEL[filters.timeWindow]}</span>
            </FilterChip>
          )}

          <div className="ml-auto flex items-center gap-3">
            <SortDropdown value={sortBy} onChange={onSortChange} />
            <div className="text-neutral-500">
              {isInitialLoad ? (
                <div
                  className="h-3 w-24 animate-pulse rounded bg-neutral-800"
                  role="status"
                  aria-label="Loading page indicator"
                />
              ) : hasError ? (
                '—'
              ) : (
                <span className="tabular-nums">
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={cn('flex-1', storeListingMissing && 'mt-4')}>
        <ReviewList
          data={displayData}
          error={error}
          isLoading={isLoading}
          onRetry={onRetry}
          hasActiveFilters={activeFilterCount > 0}
          unfilteredCount={data?.reviews.length ?? 0}
          mutatingReviewId={mutatingReviewId}
          onSaveReply={onSaveReply}
          onDeleteReply={onDeleteReply}
        />
      </div>

      {!storeListingMissing && (
        <div className="mt-4 flex items-center justify-between border-t border-white/[0.05] pt-4">
          <Button
            variant="outline"
            onClick={onPrevious}
            disabled={currentPage === 1 || isLoading}
            className="border-neutral-700 bg-neutral-950 text-neutral-200 hover:bg-neutral-900"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={onNext}
            disabled={
              isLoading ||
              !data?.pageInfo.hasNextPage ||
              currentPage >= totalPages
            }
            className="border-neutral-700 bg-white text-black hover:bg-neutral-200"
          >
            Next
          </Button>
        </div>
      )}
    </section>
  );
}

function FilterPopover({
  filters,
  onChange,
  activeCount,
}: {
  filters: ReviewFilters;
  onChange: (next: ReviewFilters) => void;
  activeCount: number;
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-2 rounded-lg border border-white/[0.06] bg-neutral-950/70 px-3 font-medium text-neutral-300 hover:bg-neutral-900 data-[state=open]:bg-neutral-900"
        >
          Filter
          {activeCount > 0 && (
            <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1.5 text-[10px] font-semibold tabular-nums text-black">
              {activeCount}
            </span>
          )}
          <TuneIcon className="size-3.5 fill-current" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          className="z-50 w-72 rounded-xl border border-white/[0.08] bg-neutral-950 p-4 text-xs text-neutral-200 shadow-xl"
        >
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
              Rating
            </div>
            <div className="grid grid-cols-1 gap-1">
              {([5, 4, 3, 2, 1] as RatingFilter[]).map((value) => {
                const checked = filters.minRating === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      onChange({
                        ...filters,
                        minRating: checked ? null : value,
                      })
                    }
                    className={cn(
                      'flex items-center justify-between rounded-md px-2 py-1.5 text-left transition-colors',
                      checked
                        ? 'bg-white/[0.08] text-white'
                        : 'hover:bg-white/[0.04]',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <ChipStars rating={value} />
                      &amp; Up
                    </span>
                    {checked && (
                      <span className="text-[10px] uppercase tracking-wider text-neutral-400">
                        Active
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
              Time period
            </div>
            <div className="grid grid-cols-3 gap-1">
              {(Object.keys(TIME_FILTER_LABEL) as TimeFilter[]).map((value) => {
                const checked = filters.timeWindow === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      onChange({
                        ...filters,
                        timeWindow: checked ? null : value,
                      })
                    }
                    className={cn(
                      'rounded-md px-2 py-1.5 transition-colors',
                      checked
                        ? 'bg-white/[0.08] text-white'
                        : 'bg-white/[0.02] text-neutral-300 hover:bg-white/[0.05]',
                    )}
                  >
                    {TIME_FILTER_LABEL[value]}
                  </button>
                );
              })}
            </div>
          </div>

          {(filters.minRating !== null || filters.timeWindow !== null) && (
            <button
              type="button"
              onClick={() => onChange({ minRating: null, timeWindow: null })}
              className="mt-4 w-full rounded-md border border-white/[0.06] py-1.5 text-neutral-400 hover:bg-white/[0.04] hover:text-white"
            >
              Clear all filters
            </button>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function FilterChip({
  children,
  onRemove,
  ariaLabel,
}: {
  children: ReactNode;
  onRemove: () => void;
  ariaLabel: string;
}) {
  return (
    <span className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-white/[0.06] bg-neutral-950/70 px-3 text-neutral-200">
      {children}
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={onRemove}
        className="-mr-1 flex size-5 items-center justify-center rounded text-neutral-500 hover:bg-white/[0.06] hover:text-neutral-200"
      >
        <CloseIcon className="size-3 fill-current" />
      </button>
    </span>
  );
}

function ChipStars({ rating }: { rating: RatingFilter }) {
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`${rating} stars`}
    >
      {range(rating).map((idx) => (
        <StarIcon
          key={idx}
          className="size-2.5 fill-current text-neutral-200"
        />
      ))}
    </span>
  );
}

function SortDropdown({
  value,
  onChange,
}: {
  value: SortOption;
  onChange: (next: SortOption) => void;
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-neutral-300 hover:bg-white/[0.04] data-[state=open]:bg-white/[0.04]"
        >
          {SORT_LABEL[value]}
          <KeyboardArrowDownIcon className="size-3.5 fill-current" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="z-50 w-44 rounded-xl border border-white/[0.08] bg-neutral-950 p-1.5 text-xs text-neutral-200 shadow-xl"
        >
          {(Object.keys(SORT_LABEL) as SortOption[]).map((option) => {
            const isSelected = option === value;
            return (
              <Popover.Close
                key={option}
                asChild
                onClick={() => onChange(option)}
              >
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left transition-colors',
                    isSelected
                      ? 'bg-white/[0.08] text-white'
                      : 'hover:bg-white/[0.04]',
                  )}
                >
                  {SORT_LABEL[option]}
                </button>
              </Popover.Close>
            );
          })}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function AnalyticsCard({
  icon,
  label,
  value,
  isLoading,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <div
        className="flex min-h-32 flex-col justify-center rounded-xl bg-neutral-900/85 p-5"
        role="status"
        aria-label={`Loading ${label}`}
      >
        <div className="size-6 animate-pulse rounded bg-neutral-800" />
        <div className="mt-5 h-7 w-20 animate-pulse rounded-md bg-neutral-800" />
        <div className="mt-2 h-3 w-28 animate-pulse rounded bg-neutral-800/70" />
      </div>
    );
  }

  return (
    <div className="flex min-h-32 flex-col justify-center rounded-xl bg-neutral-900/85 p-5 text-neutral-400">
      {icon}
      <div className="mt-5 text-2xl font-semibold tabular-nums text-white">
        {value}
      </div>
      <div className="mt-1 text-xs text-neutral-500">{label}</div>
    </div>
  );
}

function RatingScore({
  rating,
  isUnavailable,
}: {
  rating: number | null;
  isUnavailable: boolean;
}) {
  return (
    <div className="flex items-center gap-4">
      <StarIcon className="size-12 fill-white" />
      <div>
        <div className="text-6xl font-semibold leading-none tracking-normal text-white">
          {isUnavailable ? '—' : formatRating(rating)}
        </div>
        <div className="mt-2 text-sm text-neutral-500">out of 5</div>
      </div>
    </div>
  );
}

function RatingScoreSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading average rating"
      className="flex items-center gap-4"
    >
      <div className="size-12 shrink-0 animate-pulse rounded-md bg-neutral-800" />
      <div>
        <div className="h-12 w-28 animate-pulse rounded-md bg-neutral-800" />
        <div className="mt-3 h-3 w-14 animate-pulse rounded bg-neutral-800/70" />
      </div>
    </div>
  );
}

function RatingDistribution({
  buckets,
  total,
  isUnavailable,
}: {
  buckets: number[];
  total: number;
  isUnavailable?: boolean;
}) {
  const rows = [
    { stars: 5, count: buckets[4] ?? 0 },
    { stars: 4, count: buckets[3] ?? 0 },
    { stars: 3, count: buckets[2] ?? 0 },
    { stars: 2, count: buckets[1] ?? 0 },
    { stars: 1, count: buckets[0] ?? 0 },
  ];

  return (
    <div className="flex flex-col justify-center gap-2">
      {rows.map((row) => {
        const pct = total === 0 ? 0 : (row.count / total) * 100;
        return (
          <div
            key={row.stars}
            className="grid grid-cols-[4.25rem_2.5rem_1fr] items-center gap-2"
          >
            <RatingStars rating={row.stars} size="sm" />
            <span className="text-right text-xs tabular-nums text-neutral-400">
              {isUnavailable ? '—' : `${Math.round(pct)}%`}
            </span>
            <div className="h-2 overflow-hidden rounded-full bg-neutral-800">
              {!isUnavailable && (
                <div
                  className="h-full rounded-full bg-neutral-200"
                  style={{ width: `${pct}%` }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RatingDistributionSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading rating distribution"
      className="flex flex-col justify-center gap-2"
    >
      {[5, 4, 3, 2, 1].map((stars) => (
        <div
          key={stars}
          className="grid grid-cols-[4.25rem_2.5rem_1fr] items-center gap-2"
        >
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <div
                key={star}
                className="size-3 animate-pulse rounded-sm bg-neutral-800"
              />
            ))}
          </div>
          <div className="h-3 w-8 animate-pulse justify-self-end rounded bg-neutral-800/70" />
          <div className="h-2 w-full animate-pulse rounded-full bg-neutral-800" />
        </div>
      ))}
    </div>
  );
}

function ReviewList({
  data,
  error,
  isLoading,
  onRetry,
  hasActiveFilters,
  unfilteredCount,
  mutatingReviewId,
  onSaveReply,
  onDeleteReply,
}: {
  data: ReviewsPageData | null;
  error: string | null;
  isLoading: boolean;
  onRetry: () => void;
  hasActiveFilters: boolean;
  unfilteredCount: number;
  mutatingReviewId: string | null;
  onSaveReply: (review: ReviewRow, reply: string) => Promise<boolean>;
  onDeleteReply: (review: ReviewRow) => Promise<void>;
}) {
  if (isLoading && !data) {
    return (
      <div className="divide-y divide-white/[0.05]">
        {[0, 1, 2].map((index) => (
          <ReviewSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-rose-500/20 bg-rose-500/[0.06] p-5">
        <div className="text-sm font-medium text-rose-200">{error}</div>
        <Button
          variant="outline"
          onClick={onRetry}
          className="mt-4 border-rose-500/30 bg-neutral-950 text-rose-100 hover:bg-neutral-900"
        >
          Retry
        </Button>
      </div>
    );
  }

  if (data && !data.storeListingFound) {
    return (
      <EmptyState
        title="Publish this app to view ratings and reviews"
        description="Ratings and user reviews are available after the app is live in the Solana dApp Store."
      />
    );
  }

  if (!data) {
    return <EmptyState title="No review data available" />;
  }

  if (data.reviews.length === 0) {
    if (hasActiveFilters && unfilteredCount > 0) {
      return <EmptyState title="No reviews match your filters" />;
    }
    return <EmptyState title="No reviews yet" />;
  }

  return (
    <div className="divide-y divide-white/[0.05]">
      {data.reviews.map((review) => (
        <ReviewItem
          key={review.id}
          review={review}
          canReply={data.canReply}
          isMutating={mutatingReviewId === review.id}
          onSaveReply={onSaveReply}
          onDeleteReply={onDeleteReply}
        />
      ))}
    </div>
  );
}

function ReviewItem({
  review,
  canReply,
  isMutating,
  onSaveReply,
  onDeleteReply,
}: {
  review: ReviewRow;
  canReply: boolean;
  isMutating: boolean;
  onSaveReply: (review: ReviewRow, reply: string) => Promise<boolean>;
  onDeleteReply: (review: ReviewRow) => Promise<void>;
}) {
  const reviewerLabel = getReviewerLabel(review);
  const [isComposing, setIsComposing] = useState(false);
  const [replyDraft, setReplyDraft] = useState(
    review.publisherReply?.review ?? '',
  );
  const replyTextareaId = `publisher-reply-${review.id}`;
  const canWriteReply = canReply && (review.review?.trim().length ?? 0) > 0;
  const hasReply = Boolean(review.publisherReply);
  const isReplyTooLong = replyDraft.trim().length > 500;
  const isReplyEmpty = replyDraft.trim().length === 0;

  useEffect(() => {
    if (!isComposing) {
      setReplyDraft(review.publisherReply?.review ?? '');
    }
  }, [isComposing, review.publisherReply?.review]);

  const handleSubmit = () => {
    void onSaveReply(review, replyDraft).then((saved) => {
      if (saved) {
        setIsComposing(false);
      }
    });
  };

  return (
    <article className="py-6 first:pt-4 last:pb-2">
      <div className="flex gap-3">
        <ReviewerAvatar label={reviewerLabel} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-white">
                {reviewerLabel}
              </div>
              <ReviewerSubtitle review={review} />
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <RatingStars rating={review.rating} size="md" />
            <span className="text-xs tabular-nums text-neutral-500">
              {formatReviewDate(review.createdAt)}
            </span>
          </div>

          <p
            className={cn(
              'mt-3 text-sm leading-relaxed',
              review.review ? 'text-neutral-200' : 'text-neutral-500',
            )}
          >
            {review.review ?? 'No written review.'}
          </p>

          {canWriteReply && hasReply && !isComposing ? (
            <PublisherReplyCard
              reply={review.publisherReply}
              isMutating={isMutating}
              onEdit={() => setIsComposing(true)}
              onDelete={() => void onDeleteReply(review)}
            />
          ) : null}

          {canWriteReply && !hasReply && !isComposing ? (
            <button
              type="button"
              onClick={() => setIsComposing(true)}
              disabled={isMutating}
              className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-1.5 text-xs font-medium text-neutral-300 transition-colors hover:border-white/[0.16] hover:bg-white/[0.06] hover:text-white disabled:pointer-events-none disabled:opacity-50"
            >
              <ReplyIcon className="size-3.5 fill-current" />
              Reply
            </button>
          ) : null}

          {canWriteReply && isComposing ? (
            <ReplyComposer
              id={replyTextareaId}
              replyDraft={replyDraft}
              isMutating={isMutating}
              isReplyEmpty={isReplyEmpty}
              isReplyTooLong={isReplyTooLong}
              onChange={setReplyDraft}
              onCancel={() => setIsComposing(false)}
              onSubmit={handleSubmit}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}

function ReviewerSubtitle({ review }: { review: ReviewRow }) {
  return (
    <div className="mt-0.5 text-xs text-neutral-500">
      {review.isReviewerAccountDeleted
        ? 'Deleted account'
        : review.domain
          ? `via ${review.domain}`
          : review.walletAddress
            ? truncateMiddle(review.walletAddress)
            : 'Anonymous'}
    </div>
  );
}

function PublisherReplyCard({
  reply,
  isMutating,
  onEdit,
  onDelete,
}: {
  reply: ReviewRow['publisherReply'];
  isMutating: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="mt-4 rounded-xl border border-white/[0.06] bg-neutral-950/60 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-wider text-neutral-400">
            Publisher response
          </div>
          <div className="mt-1 text-xs text-neutral-500">
            {reply?.publisherName} ·{' '}
            {reply ? formatReviewDate(reply.updatedAt) : ''}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            onClick={onEdit}
            disabled={isMutating}
            className="size-8 p-0 text-neutral-300 hover:bg-white/[0.06] hover:text-white"
            aria-label="Edit publisher reply"
          >
            <EditIcon className="size-4 fill-current" />
          </Button>
          <Button
            variant="ghost"
            onClick={onDelete}
            disabled={isMutating}
            className="size-8 p-0 text-rose-200 hover:bg-rose-500/10"
            aria-label="Delete publisher reply"
          >
            <DeleteIcon className="size-4 fill-current" />
          </Button>
        </div>
      </div>
      <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-neutral-200">
        {reply?.review}
      </p>
    </div>
  );
}

function ReplyComposer({
  id,
  replyDraft,
  isMutating,
  isReplyEmpty,
  isReplyTooLong,
  onChange,
  onCancel,
  onSubmit,
}: {
  id: string;
  replyDraft: string;
  isMutating: boolean;
  isReplyEmpty: boolean;
  isReplyTooLong: boolean;
  onChange: (next: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-white/[0.08] bg-neutral-950/70 transition-colors focus-within:border-neutral-500">
      <textarea
        id={id}
        aria-label="Publisher response"
        placeholder="Write a public reply..."
        value={replyDraft}
        onChange={(event) => onChange(event.target.value)}
        disabled={isMutating}
        rows={3}
        autoFocus
        className="block w-full resize-none bg-transparent px-4 py-3 text-sm leading-relaxed text-white outline-none placeholder:text-neutral-500"
      />
      <div className="flex items-center justify-between gap-2 border-t border-white/[0.05] px-3 py-2">
        <div
          className={cn(
            'text-[11px] tabular-nums',
            isReplyTooLong ? 'text-rose-300' : 'text-neutral-500',
          )}
        >
          {replyDraft.trim().length}/500
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={isMutating}
            className="h-7 px-2.5 text-xs text-neutral-400 hover:bg-white/[0.05] hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isMutating || isReplyEmpty || isReplyTooLong}
            className="h-7 gap-1.5 rounded-full bg-white px-3 text-xs font-medium text-black shadow-none hover:bg-neutral-200 disabled:bg-white/20 disabled:text-neutral-500"
            aria-label="Send publisher reply"
          >
            <SendIcon className="size-3.5 fill-current" />
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReviewSkeleton() {
  return (
    <div className="flex gap-3 py-5">
      <div className="size-9 shrink-0 animate-pulse rounded-full bg-neutral-800" />
      <div className="flex-1 space-y-3">
        <div className="h-4 w-36 animate-pulse rounded bg-neutral-800" />
        <div className="h-3 w-24 animate-pulse rounded bg-neutral-800" />
        <div className="h-3 w-28 animate-pulse rounded bg-neutral-800" />
        <div className="h-16 animate-pulse rounded bg-neutral-800/70" />
      </div>
    </div>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-neutral-950/45 p-8 text-center">
      <div className="text-sm font-medium text-neutral-300">{title}</div>
      {description && (
        <div className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-neutral-500">
          {description}
        </div>
      )}
    </div>
  );
}

function RatingStars({ rating, size }: { rating: number; size: 'sm' | 'md' }) {
  const dimensions = size === 'md' ? 'h-4 w-4' : 'h-3 w-3';
  const clampedRating = clamp(Math.round(rating), 0, 5);

  return (
    <span className="inline-flex items-center gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((star) => (
        <StarIcon
          key={star}
          className={cn(
            dimensions,
            star <= clampedRating
              ? 'fill-white text-white'
              : 'fill-neutral-700 text-neutral-700',
          )}
        />
      ))}
    </span>
  );
}

const AVATAR_PALETTE = [
  'bg-sky-500/15 text-sky-200 ring-sky-400/20',
  'bg-emerald-500/15 text-emerald-200 ring-emerald-400/20',
  'bg-violet-500/15 text-violet-200 ring-violet-400/20',
  'bg-amber-500/15 text-amber-200 ring-amber-400/20',
  'bg-rose-500/15 text-rose-200 ring-rose-400/20',
  'bg-cyan-500/15 text-cyan-200 ring-cyan-400/20',
  'bg-fuchsia-500/15 text-fuchsia-200 ring-fuchsia-400/20',
  'bg-indigo-500/15 text-indigo-200 ring-indigo-400/20',
];

function ReviewerAvatar({ label }: { label: string }) {
  const initials = label.slice(0, 2).toUpperCase();
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  }
  const colorClasses = AVATAR_PALETTE[hash % AVATAR_PALETTE.length];

  return (
    <div
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[10px] font-medium uppercase tracking-wider ring-1 ring-inset',
        colorClasses,
      )}
    >
      {initials}
    </div>
  );
}

function getReviewerLabel(review: ReviewRow) {
  if (review.isReviewerAccountDeleted) {
    return 'Deleted reviewer';
  }

  if (review.domain) {
    return review.domain;
  }

  if (review.walletAddress) {
    return truncateMiddle(review.walletAddress);
  }

  return 'Anonymous reviewer';
}

function truncateMiddle(value: string) {
  if (value.length <= 14) {
    return value;
  }

  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function formatRating(value: number | null) {
  if (value === null || Number.isNaN(value)) {
    return '0.0';
  }

  return value.toFixed(1);
}

const reviewDateFormatter = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const fullDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

function formatReviewDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return reviewDateFormatter.format(date);
}

function formatFullDate(value: Date) {
  if (Number.isNaN(value.getTime())) {
    return null;
  }

  return fullDateFormatter.format(value);
}
